import { AsyncPipe, NgClass } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'

import { TranslatePipe } from '@ngx-translate/core'
import { Button } from 'primeng/button'
import { Tag } from 'primeng/tag'

import { Action, LanguageCode, Resource, UserAuthMethod, UserProfile } from '@ti/contracts'
import { ApiService } from '@ti/core/api'
import { Profile } from '@ti/core/auth/services/my.profile'
import { AppCommonModule } from '@ti/core/common/common.module'
import { AccessControl } from '@ti/core/common/services'
import { canManageUserGroups } from '@ti/core/common/utils'
import { DataGridSortOrder } from '@ti/core/contracts/data-grid'
import { ClockIconComponent } from '@ti/core/icons/clock'
import { NoSearchIconComponent } from '@ti/core/icons/no-search'
import { NoUsersIconComponent } from '@ti/core/icons/no-users'
import { PlusIconComponent } from '@ti/core/icons/plus'
import { LanguageService } from '@ti/core/ui-kit/i18n'
import { FormatDatePipe } from '@ti/core/ui-kit/i18n/pipes'
import { AvatarComponent } from '@ti/core/ui-kit/layout/components/avatar'
import { DataGridComponent } from '@ti/core/ui-kit/layout/components/data-grid'
import { TooltipDirective } from '@ti/core/ui-kit/layout/directives'

import { BaseUsersModel } from '../../models'
import { BaseUserManager } from '../../services'

@Component({
  selector: 'app-users-grid',
  templateUrl: 'users-grid.component.html',
  styleUrls: ['users-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AppCommonModule,
    AsyncPipe,
    AvatarComponent,
    Button,
    ClockIconComponent,
    DataGridComponent,
    FormatDatePipe,
    NgClass,
    NoSearchIconComponent,
    NoUsersIconComponent,
    PlusIconComponent,
    Tag,
    TooltipDirective,
    TranslatePipe,
  ],
})
export class UsersGridComponent implements OnInit {
  @Output() loginAsClicked = new EventEmitter<UserProfile>()
  @Output() favouriteClicked = new EventEmitter<UserProfile>()
  @Output() addNewUserClicked = new EventEmitter<void>()

  @Input() hideCurrentUser = true
  @Input() showAddUserButton = false
  @Input() loginAsAccessCheck?: [Resource, Action]
  @Input() sendInviteCheckAccess?: [Resource, Action]
  @Input() disableMfaCheckAccess?: [Resource, Action]

  protected readonly accessControl = inject(AccessControl)
  protected readonly api = inject(ApiService)
  protected readonly profile = inject(Profile)
  protected readonly currentProfile = toSignal(this.profile.state)
  protected readonly languageService = inject(LanguageService)
  protected readonly model = inject(BaseUsersModel)
  protected readonly userManager = inject(BaseUserManager)
  protected readonly TYPES = {
    Action,
    LanguageCode,
    Resource,
    UserAuthMethod,
  }

  protected _displayColumns: string[] = ['user', 'role', 'lastActivity', 'actions']

  get displayColumns(): string[] {
    return this._displayColumns
  }

  @Input()
  set displayColumns(value: string[]) {
    this._displayColumns = value
    this.model.limitDisplayColumns(this.displayColumns)
  }

  public ngOnInit() {
    this.model.hideCurrentUser = this.hideCurrentUser
    this.model.limitDisplayColumns(this.displayColumns)
  }

  protected onSortUpdated(event: DataGridSortOrder) {
    this.model.setOrder(event)
  }

  protected onLoadMoreClicked() {
    this.model.loadNextPage()
  }

  // Group-restricted users may only manage users whose groups are within their own
  protected canManage(user: UserProfile): boolean {
    return canManageUserGroups(this.currentProfile()?.groupsAccess, user.groupsAccess)
  }
}
